"use client";

import { formatPrice } from "@/utils/priceUtils";
import Image from "next/image";

import { cn } from "@/lib/utils";
import useTranslate from "@app/_hooks/useTranslate";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@ui/select";
import { ComponentProps } from "react";
import type { VariantOption } from "../types";
import { isIncomingStock } from "../utils/isIncomingStock";
import { isInStock } from "../utils/isInStock";
import { isSelectableStock } from "../utils/isSelectableStock";

type VariantSelectProps = ComponentProps<"div"> & {
  code: string;
  label: string;
  value: string;
  options: VariantOption[];
  locale: string;
  onValueChange: (value: string) => void;
} & ComponentProps<"div">;

export default function VariantSelect({
  code,
  label,
  value,
  options,
  locale,
  onValueChange,
  className,
  ...props
}: VariantSelectProps) {
  const selected = options.find((option) => option.value === value);
  const labelId = `variant-${code}-label`;

  if (!options.length) return null;

  return (
    <div data-testid="ProductVariantList" {...props}>
      <label
        id={labelId}
        htmlFor={`variant-${code}`}
        className="mb-2 block text-sm"
      >
        {label}
      </label>
      <Select value={selected?.value ?? ""} onValueChange={onValueChange}>
        <SelectTrigger
          id={`variant-${code}`}
          aria-labelledby={labelId}
          className={cn(
            "h-10 w-full cursor-pointer border-black p-0 pr-4",
            !selected?.swatch && "pl-4",
            className,
          )}
        >
          {selected ? (
            <span className="-ml-px flex min-w-0 items-center gap-3">
              {selected.swatch ? (
                <Image
                  src={selected.swatch}
                  alt=""
                  width={40}
                  height={40}
                  className="size-10 shrink-0 object-cover outline -outline-offset-1 outline-black"
                />
              ) : null}
              <span className="truncate text-base">{selected.value}</span>
            </span>
          ) : (
            <span className="truncate text-base text-gray-400">{label}</span>
          )}
        </SelectTrigger>
        <SelectContent position="popper" align="start" sideOffset={-1}>
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              textValue={option.value}
              disabled={!isSelectableStock(option.stock)}
              className="h-10 py-1 not-last:border-b"
            >
              <OptionContent option={option} locale={locale} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function OptionContent({
  option,
  locale,
}: {
  option: VariantOption;
  locale: string;
}) {
  const t = useTranslate();
  return (
    <>
      {option.swatch ? (
        <Image
          src={option.swatch}
          alt=""
          width={24}
          height={24}
          className="size-6 shrink-0 border"
        />
      ) : null}
      <span className="min-w-0 truncate text-base">{option.value}</span>
      <span className="ml-auto flex items-center gap-1 text-sm">
        {isInStock(option.stock, option.stockNumber) && (
          <>
            <output className="inline-flex size-2 shrink-0 rounded-full bg-green-500" />
            {t("inStock")}
          </>
        )}
        {isIncomingStock(option.stock) && (
          <>
            <output className="inline-flex size-2 shrink-0 rounded-full bg-green-500" />
            {t("inStockSoon")}
          </>
        )}
      </span>
      <span className="flex shrink-0 flex-col-reverse text-right">
        <span>
          {option.fromPrice ? `${t("fromShortened")} ` : null}
          {formatPrice(option.sellingPrice, locale)}
        </span>
        {option.listPrice > option.sellingPrice ? (
          <span className="text-sm text-gray-300 line-through">
            {formatPrice(option.listPrice, locale)}
          </span>
        ) : null}
      </span>
    </>
  );
}
